import * as React from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';

import Button from '../../../core/components/Button.tsx';
import Modal from '../../../core/components/Modal.tsx';
import FormField from '../../../core/components/FormField.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';

import { CREATE_SHAREPOINT_INTEGRATION, GET_SHAREPOINT_INTEGRATIONS } from '../../queries.ts';
import { CUSTOMER_INTEGRATION_SHAREPOINT_PATH } from '../../constants.ts';
import { parseRedirectUrl, url } from '../../../core/utils/link.ts';

const SharePointConnectModal = () => {
    const { t } = useTranslation('integrations');
    const { publishToast } = useToaster();
    const navigate = useNavigate();

    const form = useForm({ defaultValues: { name: '' } });

    const [createSharepointIntegration] = useMutation(CREATE_SHAREPOINT_INTEGRATION, {
        refetchQueries: [GET_SHAREPOINT_INTEGRATIONS],
    });

    const isAllowedOrigin = (url) => {
        const hostname = url.hostname;

        return (
            url.origin === window.location.origin ||
            hostname === 'login.microsoftonline.com' ||
            hostname.endsWith('.sharepoint.com')
        );
    };

    const handleSubmit = async (formData) => {
        try {
            const res = await createSharepointIntegration({
                variables: {
                    name: formData.name || undefined,
                },
            });

            const redirectUrl = res.data?.createSharepointIntegration?.redirectUrl;

            if (redirectUrl) {
                try {
                    const url = parseRedirectUrl(redirectUrl);
                    if (isAllowedOrigin(url)) {
                        window.location.href = url.toString();
                    } else {
                        console.error('Blocked unsafe redirect:', redirectUrl);
                        publishToast({
                            description: 'Invalid redirect URL',
                            status: 'error',
                        });
                    }
                } catch {
                    console.error('Invalid URL format:', redirectUrl);
                }
            }
        } catch (error) {
            console.error(error);
            publishToast({ description: t('sharepoint.connectError'), status: 'error' });
        }
    };

    return (
        <Modal
            visible={true}
            onClose={() => navigate(url(CUSTOMER_INTEGRATION_SHAREPOINT_PATH))}
            title={t('sharepoint.connectModal.title')}
            buttons={
                <>
                    <Button type="button" asChild>
                        <Link to={url(CUSTOMER_INTEGRATION_SHAREPOINT_PATH)}>
                            {t('sharepoint.connectModal.cancelButton')}
                        </Link>
                    </Button>
                    <Button variant="primary" type="submit" form="sharepoint-connect-form">
                        {t('sharepoint.connectModal.connectButton')}
                    </Button>
                </>
            }
        >
            <form
                className="flex flex-col gap-6"
                onSubmit={form.handleSubmit(handleSubmit)}
                id="sharepoint-connect-form"
            >
                <Controller
                    name="name"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('sharepoint.connectModal.name')}>
                            <StringField {...field} placeholder={t('sharepoint.connectModal.namePlaceholder')} />
                        </FormField>
                    )}
                />
            </form>
        </Modal>
    );
};

export default SharePointConnectModal;
